import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Check, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { NONE } from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Notification = {
  id: number
  event_code: string
  subject: string
  body_text: string
  body_html: string
  link_to: string
  channels_sent: string[]
  email_sent_at: string | null
  email_error: string
  is_read: boolean
  read_at: string | null
  created_at: string
}

type EventDef = {
  code: string
  label: string
  category: string
}

function relativeTime(iso: string): string {
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  return new Date(iso).toLocaleString()
}

// ─────────────────── Page ───────────────────

export default function MyNotifications() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>('unread')
  const [categoryFilter, setCategoryFilter] = useState<string>('')

  const queryString = (() => {
    const p = new URLSearchParams()
    if (readFilter === 'unread') p.set('is_read', 'false')
    if (readFilter === 'read') p.set('is_read', 'true')
    return p.toString()
  })()

  const notifsQ = useQuery({
    queryKey: ['notifications', 'list', queryString],
    queryFn: async () =>
      (await api.get<Notification[]>(`/notifications/${queryString ? '?' + queryString : ''}`)).data,
  })

  // Event registry — needed to map event_code → category for the filter.
  // We use the admin endpoint, but it requires the rules-admin permission;
  // for non-admins this will 403 and we just hide the category filter.
  const eventsQ = useQuery({
    queryKey: ['notifications', 'events'],
    queryFn: async () => (await api.get<EventDef[]>('/notifications/events/')).data,
    retry: false,
  })
  const categoryOf = new Map<string, string>()
  for (const e of eventsQ.data || []) categoryOf.set(e.code, e.category)

  const markReadMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/notifications/${id}/mark-read/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
  const markAllReadMut = useMutation({
    mutationFn: async () => (await api.post('/notifications/mark-all-read/')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  function onRowClick(n: Notification) {
    if (!n.is_read) markReadMut.mutate(n.id)
    if (n.link_to) navigate(n.link_to)
  }

  const allNotifs = notifsQ.data || []
  const notifs = categoryFilter
    ? allNotifs.filter((n) => categoryOf.get(n.event_code) === categoryFilter)
    : allNotifs

  const unreadCount = allNotifs.filter((n) => !n.is_read).length

  const categories = Array.from(new Set(
    (eventsQ.data || []).map((e) => e.category),
  )).sort()

  return (
    <AdminLayout>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" /> Notifications
          </h2>
          <p className="text-sm text-muted-foreground">
            {allNotifs.length} {readFilter === 'unread' ? 'unread' : readFilter === 'read' ? 'read' : 'total'}
            {' · '}
            All in-app notifications you've received. Click a row to mark it read and open the linked record.
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline" size="sm"
            onClick={() => markAllReadMut.mutate()}
            disabled={markAllReadMut.isPending}
          >
            <Check className="h-3 w-3 mr-1" /> Mark all read
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap gap-2 items-center text-sm">
        <div className="inline-flex gap-1 text-xs bg-muted/40 rounded p-0.5">
          {(['unread', 'all', 'read'] as const).map((k) => (
            <button
              key={k} type="button"
              onClick={() => setReadFilter(k)}
              className={`px-3 py-1 rounded ${readFilter === k ? 'bg-card shadow font-semibold' : 'text-muted-foreground'}`}
            >
              {k === 'unread' ? 'Unread' : k === 'all' ? 'All' : 'Read'}
            </button>
          ))}
        </div>
        {categories.length > 0 && (
          <Select
            value={categoryFilter || NONE}
            onValueChange={(v) => setCategoryFilter(v === NONE ? '' : v)}
          >
            <SelectTrigger className="w-44 h-8 text-xs">
              <SelectValue placeholder="Any category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Any category</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {notifsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : notifs.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          {readFilter === 'unread' ? "You're all caught up." : 'No notifications match this filter.'}
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <ul className="divide-y divide-border">
            {notifs.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => onRowClick(n)}
                  className={`w-full text-left px-4 py-3 hover:bg-muted/40 ${
                    !n.is_read ? 'bg-violet-50/40' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {!n.is_read ? (
                      <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-violet-600 flex-shrink-0" />
                    ) : (
                      <span className="mt-2 inline-block w-1.5 h-1.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-violet-100 text-violet-700">
                          {categoryOf.get(n.event_code) || n.event_code.split('.')[0]?.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium truncate">{n.subject}</span>
                      </div>
                      {n.body_text && (
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-3">
                          {n.body_text}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                        <span>{relativeTime(n.created_at)}</span>
                        <span className="font-mono opacity-60">{n.event_code}</span>
                        {n.channels_sent.includes('EMAIL') && (
                          <span className={n.email_sent_at ? 'text-green-700' : n.email_error ? 'text-red-600' : ''}>
                            email {n.email_sent_at ? 'sent' : n.email_error ? '(failed)' : 'queued'}
                          </span>
                        )}
                      </div>
                    </div>
                    {n.link_to && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AdminLayout>
  )
}
